import React from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import DialogContent from '@material-ui/core/DialogContent'
import Button from '@material-ui/core/Button'
import Icons from '../icons/icons'
import './modal.scss'
/**
 * @modified : CTBL-338-change on the variation breakdown
 * @description
 * A reusable modal/dialog component built using Material-UI's Dialog elements.
 * This component supports dynamic content, configurable buttons, and conditional
 * rendering of different sections such as headers, actions, and footers.
 * 
 * @props
 * - open (boolean): Controls the visibility of the modal.
 * - onClose (function): Callback triggered when the modal is closed.
 * - modalHeading (string): Text displayed in the modal header.
 * - modalTitle (string): Optional title displayed above the content.
 * - modalContent (ReactNode): Custom content/body of the modal.
 * - children (ReactNode): Fallback content if modalContent is not provided.
 * - fullWidth (boolean): Whether the modal should take up full width.
 * - maxWidth (string): Specifies the maximum width of the modal (e.g., 'sm', 'md').
 * - disableBackdropClick (boolean): Prevents modal close on backdrop click if true.
 * - hideCloseIcon (boolean): Hides the close icon in the header if true.
 * 
 * Button Controls:
 * - showActionBtn (boolean): Displays the standard action button section.
 * - footerBtns (boolean): Displays the enhanced footer buttons (e.g., PDF/Word download).
 * - additionalbtnTxt (string): Label for additional button.
 * - additionalbtn (function): Callback for additional button.
 * - modalSaveTxt (string): Label for save button.
 * - onClick (function): Callback for save or cancel button.
 * - modalEditTxt (string): Label for edit button.
 * - onEdit (function): Callback for edit action.
 * - modalCloseTxt (string): Label for close button.
 * - modalCloseBtn (function): Optional separate callback for close button.
 * - modalPdfTxt (string): Label for PDF download button.
 * - onPdfClick (function): Callback for PDF download.
 * - modalWordTxt (string): Label for Word download button.
 * - onWordClick (function): Callback for Word download.
 * - cancel (string): Label for custom cancel button in footer.
 * - save (string): Label for custom save button in footer.
 * 
 * @styles
 * Uses SCSS styles from `modal.scss` for layout and theming.
 * 
 * @dependencies
 * - Material-UI core components (Dialog, Grid, Button)
 * - Custom `Icons` component for rendering the close icon
 */
export default function Modal(props) {
  const handleClose = (event, reason) => {
    if (!props.disableBackdropClick && reason === "backdropClick") {
      return false;
    }
    if (typeof onClose === "function") {
      props.onClose();
    }
  };
  return (
    <div className='modalContainer'>
      <Dialog
        className={`${props.primary ? 'primary' : ''} ${props.className || ''}`}
        onClose={handleClose}
        open={props.open || false}
        aria-labelledby='form-dialog-title'
        fullWidth={props.fullWidth}
        maxWidth={props.maxWidth}
        disableEnforceFocus
        data-cy={props.dataCy || props['data-cy'] || 'modal-dialog'}
      >
        <DialogTitle id='form-dialog-title'>
          <Grid container className='titlebg'>
            <Grid data-cy='modal-title' item sm={11} xs={10}>{props.modalHeading}</Grid>
            {!props.hideCloseIcon && <Grid item sm={1} xs={2} align='right'><Icons icon='cancel' onClick={props.onClose} /></Grid>}
          </Grid>
        </DialogTitle>
        <DialogContent onScroll={props.onScroll} className='modal-body'>
          <div className='title'>{props.modalTitle}</div>
          <div className='content'>{props.modalContent || props.children}</div>
        </DialogContent>
        {props.showActionBtn &&
          <DialogActions className='actionbtn'>
            {props.additionalbtnTxt && <Button variant='contained' className={props.savebtnClass + ' listbtn'} onClick={props.additionalbtn} color='primary'>{props.additionalbtnTxt}</Button>}
            <div className='actionbtn'>
              {props.additionalTxt && <small>{props.additionalTxt}</small>}
              {props.modalSaveTxt && <Button data-cy="save-text" variant='contained' className={`cancel-btn ${props.modalSaveTxtDisabledClass || ''}`} onClick={props.onClick} color='primary'>{props.modalSaveTxt}</Button>}
              {props.modalEditTxt && <Button data-cy="edit-text" variant='contained' className='cancel-btn' onClick={props.onEdit} >{props.modalEditTxt}</Button>}
              {props.modalCloseTxt && <Button data-cy="close-text" variant='contained' className='cancel-btn' onClick={props.modalCloseBtn || props.onClose}>{props.modalCloseTxt}</Button>}
            </div>
          </DialogActions>}
        {props.footerBtns &&
          <DialogActions className='actionbtn'>
            {props.additionalbtnTxt && <Button variant='contained' className={props.savebtnClass + ' listbtn'} onClick={props.additionalbtn} color='primary'>{props.additionalbtnTxt}</Button>}
            <div className='actionbtn'>
              {props.additionalTxt && <small>{props.additionalTxt}</small>}
              {props.modalSaveTxt && <Button data-cy="save-text" variant='contained' className={`cancel-btn ${props.modalSaveTxtDisabledClass || ''}`} onClick={props.onClick} color='primary'>{props.modalSaveTxt}</Button>}
              {props.modalEditTxt && <Button data-cy="edit-text" variant='contained' className='cancel-btn' onClick={props.onEdit} >{props.modalEditTxt}</Button>}
              {props.modalCloseTxt && <Button data-cy="close-text" variant='contained' className='cancel-btn' onClick={props.modalCloseBtn || props.onClose}>{props.modalCloseTxt}</Button>}
              {props.modalPdfTxt && (<Button variant='contained' className="secondarybtn me-3 btn btn-primary" onClick={props.onPdfClick} color='primary'>{props.modalPdfTxt}</Button>)}
              {props.modalWordTxt && (<Button variant='contained' className="secondarybtn btn btn-primary" onClick={props.onWordClick} color='primary' data-cy="word-download">{props.modalWordTxt}</Button>)}
            </div>
          </DialogActions>}

        <DialogActions className='actionbtn'>

          <div className='footer-btns'>
            {props.footerAdditionalbtnTxt && <Button variant='contained' className='listbtn footer-additional-btn' onClick={props.footerAdditionalbtn} color='primary'>{props.footerAdditionalbtnTxt}</Button>}
            {props.cancel && <Button variant='contained' className='cancel-btn' onClick={props.onClick} color='primary'>{props.cancel}</Button>}
            {props.save && <Button variant='contained' className='cancel-btn' onClick={props.onEdit} >{props.save}</Button>}

          </div>
        </DialogActions>
      </Dialog>
    </div>
  )
}
